import { Box, Paper, TablePagination } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FullscreenSpinner } from '../feedback/circular';
import { TableColumn } from './DTO';
import { FormattedTable } from './FormattedTable';
import { JsonDisplay } from './JsonDisplay';
import { DateRange, FilterCalcOrder, FilterControl, SortBy, TableDisplay } from './filters/DTO';
import TableFilters from './filters/TableFilters';

/* FilteredTable can either be filtered (controlled) externally or locally
	if controlled:
		filtering + pagination is sent through "getEntries",
	if not controlled:
		entries are initially supplied through "allEntries" prop and saved to "entries",
		filtering + pagination is handled locally
	In either case, results are saved to "filteredEntries"
*/

type FilteredTableProps<T> = {
	columns?: TableColumn<T>[]
	filterControls?: FilterControl[]
	getEntries?: (filters: Record<string, unknown>, callback: (data: T[]) => void) => void
	getEntriesCount?: ((filters: Record<string, unknown>, callback: (count: number) => void) => void)
	allEntries?: T[]
	pagination?: boolean
	wrapTable?: boolean
	editorHeight?: string
	editorWidth?: string
	maxTableHeight?: string
}

export const FilteredTable = <T extends Record<string, unknown>>({
	columns,
	filterControls,
	getEntries,
	getEntriesCount,
	allEntries,
	pagination = true,
	editorHeight,
	editorWidth,
	maxTableHeight
}: FilteredTableProps<T>) => {
	const entries = useMemo(() => allEntries?.filter((entry) => !!entry), [allEntries])

	const [filteredEntries, setFilteredEntries] = useState<T[] | undefined>(undefined)
	const [filteredEntriesCount, setFilteredEntriesCount] = useState<number>(0)
	const [orderedEntries, setOrderedEntries] = useState<T[] | undefined>(undefined)

	const [filters, setFilters] = useState<Record<string, unknown>>(
		filterControls ?
			filterControls.filter((c) => !c.isDisplaySetting)
				.reduce((acc, filter) =>
					({ ...acc, [filter.name]: filter.value })
					, {})
			: {}
	)
	const [displaySettings, setDisplaySettings] = useState<Record<string, unknown>>(() => {
		const displaySettingsObject: Record<string, unknown> = {}
		if (filterControls)
			filterControls.filter((c) => c.isDisplaySetting).forEach((filter) => displaySettingsObject[filter.name] = filter.value)
		if (pagination) {
			displaySettingsObject.pageSize = 10
			displaySettingsObject.pageNumber = 0
		}
		const sortByAge = filterControls?.find((c) => c.type === 'sort by age')
		if (sortByAge && !sortByAge.value)
			displaySettingsObject[sortByAge.name] = SortBy.Newest
		return displaySettingsObject
	})

	const setPageNumber = (n: number) => setDisplaySettings({ ...displaySettings, pageNumber: n })

	const controlled = !!getEntries && !!getEntriesCount

	const filtersRef = useRef(filters);
	filtersRef.current = filters;

	const getFilterFunc = (type: string) => {
		switch (type) {
			case 'date range':
				return (val: string | number | Date, filter: DateRange) =>
					(new Date(val) > filter.start) && (new Date(val) < filter.end)

			case 'search':
				return (val: string | number | Date | string[] | number[] | Date[], filter: string) =>
					Array.isArray(val) ?
						val.some((v) => String(v).toUpperCase().includes(filter.toUpperCase()))
						: String(val).toUpperCase().includes(filter.toUpperCase())

			case 'log level':
			case 'multiselect':
				return (val: string | number, filter: string[] | number[]) =>
					typeof filter[0] === 'number' ?
						(filter as number[]).includes(Number(val))
						: (filter as string[]).includes(String(val))

			case 'sort by age':
			case 'table display':
			case 'select':
			default:
				return (val: string | number, filter: string | number) => val == filter
		}
	}

	const filtersInfo = useMemo(() =>
		filterControls ?
			// filtering is done in this order to try to filter out the most entries up front to improve performance
			[...filterControls].sort((a, b) =>
				FilterCalcOrder[a.type as keyof typeof FilterCalcOrder]
				- FilterCalcOrder[b.type as keyof typeof FilterCalcOrder]
			)
				.map((filter) => ({
					name: filter.name,
					column: filter.column ?? filter.name,
					filterFunc: (entry: T) => {
						if (filter.filterEntry)
							return filter.filterEntry(entry, filtersRef.current[filter.name])

						const column = filter.column ?? filter.name
						//filters affecting multiple columns is currently only implemented for search
						const val = Array.isArray(column) ? column.map((c) => entry[c]) : entry[column]
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
						return getFilterFunc(filter.type)(val as any, filtersRef.current[filter.name] as any)
					}
				}))
			: []
		, [filterControls])

	const handleUpdateFilter = (name: string, value: unknown) => {
		if (filterControls?.find((c) => c.name === name)?.isDisplaySetting)
			setDisplaySettings((prevDisplaySettings) => ({
				...prevDisplaySettings,
				[name]: value,
				pageNumber: 0
			}))
		else {
			setFilters((prevFilters) => ({
				...prevFilters,
				[name]: value
			}))
			setPageNumber(0)
		}
	}

	const fetchEntriesIfControlled = () => {
		if (!controlled) return
		getEntries(
			{ ...filters, ...displaySettings },
			setOrderedEntries
		)
		getEntriesCount(filters, setFilteredEntriesCount)
	}

	useEffect(() => {
		fetchEntriesIfControlled()
		if (controlled || !entries || !filters || !filtersInfo)
			return

		// local filtering
		const keys = Object.keys(filters)
		const curFiltersInfo = filtersInfo.filter((f) =>
			filters[f.name] !== undefined && keys.includes(f.name)
		)
		setFilteredEntries(entries.filter((e) =>
			curFiltersInfo.every((f) => f.filterFunc(e))
		))
	}, [filters, entries])

	useEffect(() => {
		fetchEntriesIfControlled()
		if (controlled || Object.keys(displaySettings).length === 0 || !filteredEntries)
			return
		if (filteredEntries.length === 0 && orderedEntries && orderedEntries.length > 0) {
			setOrderedEntries([])
			return
		}

		// maybe add a more generic sort handler
		let curOrderedEntries = [...filteredEntries]
		const sortByAgeFilter = filterControls?.find((f) => f.type === 'sort by age')
		if (sortByAgeFilter) {
			const val = displaySettings[sortByAgeFilter.name]
			const col = sortByAgeFilter.column as string ?? sortByAgeFilter.name
			curOrderedEntries.sort((a, b) => {
				const aDate = (a[col] as Date).getTime()
				const bDate = (b[col] as Date).getTime()
				return val === 'Oldest' ? bDate - aDate : aDate - bDate
			})
		}

		if (pagination) {
			setFilteredEntriesCount(curOrderedEntries.length)
			const size = displaySettings.pageSize as number
			const number = displaySettings.pageNumber as number
			curOrderedEntries = curOrderedEntries.slice(size * number, size * (number + 1))
		}

		setOrderedEntries(curOrderedEntries)
	}, [filteredEntries, displaySettings])

	const displayFilterControls = useMemo(() => (
		filterControls ?
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				padding={1}
				gap={2}
				width="100%"
			>
				<TableFilters
					filters={filterControls}
					onChange={handleUpdateFilter}
				/>
			</Box>
			: null
	), [filterControls])

	const displayTable = useMemo(() => {
		if ((displaySettings.display ?? 0) as TableDisplay === TableDisplay.Json)
			return (
				<JsonDisplay
					entries={orderedEntries ?? filteredEntries ?? []}
					height={editorHeight}
					width={editorWidth}
				/>
			)
		return (
			<FormattedTable
				columns={columns}
				entries={orderedEntries ?? filteredEntries ?? []}
				maxHeight={maxTableHeight}
			/>
		)
	}, [displaySettings.display, filteredEntries, orderedEntries])

	return (
		(orderedEntries || filteredEntries) ?
			<Box
				display="flex"
				flexDirection="column"
				alignItems="flex-start"
				width="100%"
				component={Paper}
				height="fit-content"
				overflow="auto"
			>
				{displayFilterControls}
				{displayTable}
				{pagination &&
					<TablePagination
						style={{ marginLeft: "auto" }}
						component="div"
						count={filteredEntriesCount}
						page={displaySettings.pageNumber as number}
						onPageChange={(_, pageNumber) => setPageNumber(pageNumber)}
						rowsPerPage={displaySettings.pageSize as number}
						onRowsPerPageChange={(evt) => {
							setDisplaySettings({ ...displaySettings, pageSize: parseInt(evt.target.value, 10), pageNumber: 0 })
						}}
					/>
				}
			</Box>
			: <FullscreenSpinner />
	)
}